import React, { useState, useRef, useEffect } from "react";
import { Volume2, Play, Pause, X, FileText, Loader2 } from "lucide-react";
import { useI18n } from "../i18n";
import { useAuth } from "../context/AuthContext";
import api from "../api";

// §4.1 Summarize & Read-Aloud: role-scoped 4–6 sentence spoken summary (not raw records),
// with play/pause/speed controls, transcript fallback, and its own logging (backend /summary).
export default function ReadAloud({ screen }) {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [text, setText] = useState("");
  const [speaking, setSpeaking] = useState(false);
  const [rate, setRate] = useState(1);
  const utterRef = useRef(null);

  const stop = () => {
    if (window.speechSynthesis) window.speechSynthesis.cancel();
    setSpeaking(false);
  };

  useEffect(() => { stop(); setText(""); setOpen(false); }, [screen, lang]);
  useEffect(() => () => stop(), []);

  const speak = (content) => {
    if (!window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(content);
    u.lang = lang === "ar" ? "ar-SA" : "en-US";
    u.rate = rate;
    u.onend = () => setSpeaking(false);
    utterRef.current = u;
    window.speechSynthesis.speak(u);
    setSpeaking(true);
  };

  const handleOpen = async () => {
    setOpen(true);
    if (text) return;
    setLoading(true);
    try {
      const res = await api.summary(screen || "dashboard", user?.role || "Chief Security Officer", lang);
      setText(res.summary);
      setTimeout(() => speak(res.summary), 250);
    } catch (e) {
      setText("Summary unavailable right now.");
    } finally {
      setLoading(false);
    }
  };

  const togglePlay = () => {
    if (speaking) { stop(); }
    else if (text) { speak(text); }
  };

  const changeRate = (r) => {
    setRate(r);
    if (speaking && text) speak(text);
  };

  return (
    <>
      <button
        onClick={handleOpen}
        aria-label={t("readAloud")}
        className="fixed bottom-6 end-6 z-40 w-14 h-14 rounded-full bg-teal-700 hover:bg-teal-800 text-white shadow-lg shadow-teal-700/30 flex items-center justify-center transition-transform hover:scale-105"
      >
        <Volume2 className="w-6 h-6" />
      </button>

      {open && (
        <div className="fixed bottom-24 end-6 z-40 w-[360px] max-w-[calc(100vw-3rem)] card-surface p-4 animate-fade-in">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-teal-50 flex items-center justify-center"><Volume2 className="w-4 h-4 text-teal-700" /></div>
              <span className="font-semibold text-slate-900">{t("readAloud")}</span>
            </div>
            <button onClick={() => { stop(); setOpen(false); }} className="w-7 h-7 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-500"><X className="w-4 h-4" /></button>
          </div>

          {loading ? (
            <div className="flex items-center gap-2 text-slate-500 text-sm py-6 justify-center">
              <Loader2 className="w-4 h-4 animate-spin" /> {t("generating")}
            </div>
          ) : (
            <>
              <div className="flex items-center gap-2 mb-3">
                <button onClick={togglePlay} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-teal-700 hover:bg-teal-800 text-white text-sm font-medium">
                  {speaking ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                  {speaking ? t("pause") : t("play")}
                </button>
                <div className="flex items-center gap-1.5 ms-auto">
                  <span className="text-xs text-slate-500">{t("speed")}</span>
                  {[0.75, 1, 1.25, 1.5].map((r) => (
                    <button key={r} onClick={() => changeRate(r)} className={`px-1.5 py-0.5 rounded text-xs font-medium ${rate === r ? "bg-teal-700 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>{r}x</button>
                  ))}
                </div>
              </div>
              <div className="flex items-center gap-1.5 text-xs text-slate-400 mb-1.5"><FileText className="w-3.5 h-3.5" />{t("transcript")}</div>
              <p className="text-sm text-slate-700 leading-relaxed max-h-40 overflow-y-auto">{text}</p>
            </>
          )}
        </div>
      )}
    </>
  );
}
